import React, { useState, useEffect, useCallback } from 'react';
import { integrationAppsService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO } from '../design';

const SWAGGER_URL = 'https://financeiro.ipcom.com.br/api/docs';

const AVAILABLE_SCOPES = [
  { value: 'clients:read', label: 'Clientes (leitura)' },
  { value: 'contracts:read', label: 'Contratos (leitura)' },
  { value: 'invoices:read', label: 'Faturas (leitura)' },
  { value: 'products:read', label: 'Produtos (leitura)' },
  { value: 'contacts:read', label: 'Contatos (leitura)' },
  { value: 'boletos:read', label: 'Boletos / 2ª via (leitura)', sensitive: true },
  { value: 'fiscal:read', label: 'Notas Fiscais (leitura)', sensitive: true },
];

// Scopes financeiros: o backend EXIGE allowlist de IP + (allowedClientIds OU
// unrestrictedBillingDocumentsAccess). Sem isso o save é rejeitado (403/400).
const SENSITIVE_BILLING_SCOPES = ['boletos:read', 'fiscal:read'];

const hasSensitiveScope = (scopes: string[]): boolean =>
  scopes.some((s) => SENSITIVE_BILLING_SCOPES.includes(s));

// "1, 2, 305" ou um por linha → [1, 2, 305] (ints positivos, dedup)
const parseClientIds = (raw: string): number[] =>
  Array.from(
    new Set(
      (raw || '')
        .split(/[\n,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0),
    ),
  );

// Cor (11/10/2026, PR-2 da medição): estado da app no mapa de tons.
const statusColors: Record<string, { bg: string; text: string }> = {
  active: { bg: TONE_BADGE.open, text: '' },
  revoked: { bg: TONE_BADGE.danger, text: '' },
  expired: { bg: TONE_BADGE.warning, text: '' },
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  revoked: 'Revogado',
  expired: 'Expirado',
};

type ModalKind = 'create' | 'edit' | 'key' | 'revoke' | 'rotate' | null;

interface IntegrationApp {
  id: number;
  name: string;
  description: string | null;
  scopes: string[];
  allowedIps: string[] | null;
  allowedClientIds?: number[] | null;
  unrestrictedBillingDocumentsAccess?: boolean;
  rateLimitPerMinute: number;
  status: string;
  keyHint: string;
  lastUsedAt: string | null;
  totalRequests: number;
  expiresAt: string | null;
  createdAt: string;
  revokedAt?: string | null;
  revokeReason?: string | null;
  createdBy: { id: number; name: string } | null;
}

type NotificationType = 'success' | 'error' | 'info';
interface Notification {
  message: string;
  type: NotificationType;
}

function extractError(e: any, fallback: string): string {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string') return data.message;
  if (Array.isArray(data.message)) return data.message.join(', ');
  if (typeof data.message?.detail === 'string') return data.message.detail;
  if (typeof data.detail === 'string') return data.detail;
  return fallback;
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function dateInputToIso(value: string): string | null {
  if (!value) return null;
  return new Date(value + 'T12:00:00').toISOString();
}


export default function IntegrationsTab() {
  const [apps, setApps] = useState<IntegrationApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalKind>(null);
  const [selectedApp, setSelectedApp] = useState<IntegrationApp | null>(null);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  // D5 (11/10/2026): a lista que não carregava dava um toast que some e "Nenhuma integração cadastrada. Crie uma app…";
  // o operador gerava uma chave nova em duplicidade, e chave viva esquecida é superfície.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;

  const emptyForm = {
    name: '',
    description: '',
    scopes: [] as string[],
    allowedIps: '',
    allowedClientIds: '',
    unrestrictedBillingDocumentsAccess: false,
    rateLimitPerMinute: 60,
    expiresAt: '',
  };
  const [form, setForm] = useState(emptyForm);

  const notify = useCallback((message: string, type: NotificationType = 'info') => {
    setNotification({ message, type });
  }, []);

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(t);
  }, [notification]);

  const closeModal = useCallback(() => {
    setModal(null);
    setNewKey('');
    setCopied(false);
    setRevokeReason('');
    setSelectedApp(null);
  }, []);

  // O Esc dos modais é da CascaDeModal (09/10/2026): o listener próprio daqui fechava o formulário por fora da pilha,
  // pulando o "Fechar sem salvar?" e o modal de cima.

  const loadData = useCallback(async () => {
    setLoading(true);
    await rodarCarga(async () => {
      const data = await integrationAppsService.getAll();
      setApps(Array.isArray(data) ? data : []);
    });
    setLoading(false);
  }, [rodarCarga]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!form.name || form.scopes.length === 0) return;
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        scopes: form.scopes,
        rateLimitPerMinute: form.rateLimitPerMinute || 60,
      };
      if (form.description) payload.description = form.description;
      if (form.allowedIps.trim()) {
        payload.allowedIps = form.allowedIps.split('\n').map((ip) => ip.trim()).filter(Boolean);
      }
      const clientIds = parseClientIds(form.allowedClientIds);
      if (clientIds.length) payload.allowedClientIds = clientIds;
      if (form.unrestrictedBillingDocumentsAccess) payload.unrestrictedBillingDocumentsAccess = true;
      const iso = dateInputToIso(form.expiresAt);
      if (iso) payload.expiresAt = iso;

      const result = await integrationAppsService.create(payload);
      setNewKey(result.key);
      setModal('key');
      await loadData();
      notify('Integração criada com sucesso', 'success');
    } catch (e: any) {
      notify(extractError(e, 'Erro ao criar integração'), 'error');
    }
    setSaving(false);
  };

  const handleEdit = async () => {
    if (!selectedApp || !form.name) return;
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        description: form.description || null,
        scopes: form.scopes,
        rateLimitPerMinute: form.rateLimitPerMinute,
      };
      if (form.allowedIps.trim()) {
        payload.allowedIps = form.allowedIps.split('\n').map((ip) => ip.trim()).filter(Boolean);
      } else {
        payload.allowedIps = null;
      }
      const clientIds = parseClientIds(form.allowedClientIds);
      payload.allowedClientIds = clientIds.length ? clientIds : null;
      payload.unrestrictedBillingDocumentsAccess = form.unrestrictedBillingDocumentsAccess;
      // expiresAt: string ISO ou null (remover expiração)
      payload.expiresAt = dateInputToIso(form.expiresAt);

      await integrationAppsService.update(selectedApp.id, payload);
      closeModal();
      await loadData();
      notify('Integração atualizada', 'success');
    } catch (e: any) {
      notify(extractError(e, 'Erro ao atualizar'), 'error');
    }
    setSaving(false);
  };

  const handleRevoke = async () => {
    if (!selectedApp) return;
    setSaving(true);
    try {
      await integrationAppsService.revoke(selectedApp.id, revokeReason || undefined);
      closeModal();
      await loadData();
      notify(`"${selectedApp.name}" revogada`, 'success');
    } catch (e: any) {
      notify(extractError(e, 'Erro ao revogar'), 'error');
    }
    setSaving(false);
  };

  const handleRotateKey = async () => {
    if (!selectedApp) return;
    setSaving(true);
    try {
      const result = await integrationAppsService.rotateKey(selectedApp.id);
      setNewKey(result.key);
      setModal('key');
      await loadData();
      notify('Nova chave gerada. A anterior fica válida por 24h.', 'success');
    } catch (e: any) {
      notify(extractError(e, 'Erro ao rotacionar chave'), 'error');
    }
    setSaving(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = newKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openCreateModal = () => {
    setForm(emptyForm);
    setSelectedApp(null);
    setModal('create');
  };

  const openEditModal = (app: IntegrationApp) => {
    setSelectedApp(app);
    setForm({
      name: app.name,
      description: app.description || '',
      scopes: app.scopes || [],
      allowedIps: (app.allowedIps || []).join('\n'),
      allowedClientIds: (app.allowedClientIds || []).join(', '),
      unrestrictedBillingDocumentsAccess: !!app.unrestrictedBillingDocumentsAccess,
      rateLimitPerMinute: app.rateLimitPerMinute,
      expiresAt: toDateInputValue(app.expiresAt),
    });
    setModal('edit');
  };

  const openRevokeModal = (app: IntegrationApp) => {
    setSelectedApp(app);
    setRevokeReason('');
    setModal('revoke');
  };

  const openRotateModal = (app: IntegrationApp) => {
    setSelectedApp(app);
    setModal('rotate');
  };

  const toggleScope = (scope: string) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const visibleApps = showRevoked
    ? apps
    : apps.filter((app) => app.status !== 'revoked');

  const revokedCount = apps.filter((app) => app.status === 'revoked').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">API de Integrações</h2>
          <p className="text-sm text-slate-400 mt-1">
            Gerencie as chaves de API para integrações externas (CRM, parceiros).{' '}
            <a
              href={SWAGGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-200 hover:text-white underline"
            >
              Ver documentação da API
            </a>
          </p>
        </div>
        {carga.estado === 'pronto' && (
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Nova App
        </button>
        )}
      </div>

      {/* Filter toggle pra revogadas */}
      {revokedCount > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => setShowRevoked((v) => !v)}
            className="text-slate-400 hover:text-slate-200 underline underline-offset-2"
          >
            {showRevoked
              ? `Ocultar revogadas (${revokedCount})`
              : `Mostrar revogadas (${revokedCount})`}
          </button>
        </div>
      )}

      {/* Apps List */}
      <EstadoDaLista carga={carga} temDados={carga.atualizadoEm !== null} oQue="as integrações" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      {loading ? (
        <div className="text-center py-12 text-slate-400">Carregando...</div>
      ) : visibleApps.length === 0 ? (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
          <p className="text-slate-400">
            {apps.length === 0 ? 'Nenhuma integração cadastrada.' : 'Nenhuma integração ativa.'}
          </p>
          <p className="text-slate-500 text-sm mt-1">
            {apps.length === 0 ? 'Crie uma app para gerar uma chave de API.' : null}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleApps.map((app) => {
            const statusStyle = statusColors[app.status] || statusColors.active;
            const isRevoked = app.status === 'revoked';
            return (
              <div
                key={app.id}
                className={`bg-slate-800 rounded-xl border p-5 ${
                  isRevoked ? 'border-red-900/40 opacity-70' : 'border-slate-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-white font-medium text-lg">{app.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        {statusLabels[app.status] || app.status}
                      </span>
                      <span className="text-slate-500 text-xs font-mono">{app.keyHint}</span>
                      {app.expiresAt && !isRevoked && (
                        <span className="text-xs text-slate-500">
                          Expira em {fmtDateTime(app.expiresAt)}
                        </span>
                      )}
                    </div>
                    {app.description && (
                      <p className="text-slate-400 text-sm mb-3">{app.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(app.scopes || []).map((scope) => (
                        <span key={scope} className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs font-mono">
                          {scope}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
                      <span>Requests: <span className="text-slate-300">{app.totalRequests.toLocaleString('pt-BR')}</span></span>
                      <span>Rate limit: <span className="text-slate-300">{app.rateLimitPerMinute}/min</span></span>
                      {app.lastUsedAt && (
                        <span>Último uso: <span className="text-slate-300">{fmtDateTime(app.lastUsedAt)}</span></span>
                      )}
                      {app.createdBy && (
                        <span>Criado por: <span className="text-slate-300">{app.createdBy.name}</span></span>
                      )}
                    </div>
                    {isRevoked && (
                      <div className="mt-3 pt-3 border-t border-red-900/30 text-xs text-red-300/80">
                        Revogada{app.revokedAt && ` em ${fmtDateTime(app.revokedAt)}`}
                        {app.revokeReason && (
                          <span> · Motivo: <span className="text-red-200">{app.revokeReason}</span></span>
                        )}
                      </div>
                    )}
                  </div>
                  {app.status === 'active' && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => openEditModal(app)}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button
                        onClick={() => openRotateModal(app)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Rotacionar chave"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      </button>
                      <button
                        onClick={() => openRevokeModal(app)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                        title="Revogar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </EstadoDaLista>

      {/* Create / Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        // Formulário (scopes, IPs, clientes): antes fechava ao clicar fora; agora só
        // pelo × ou Cancelar.
        <CascaDeModal
          largura="sm"
          titulo={modal === 'create' ? 'Nova Integration App' : 'Editar Integration App'}
          onClose={closeModal}
          corpoClassName="p-6 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={modal === 'create' ? handleCreate : handleEdit}
                disabled={saving || !form.name || form.scopes.length === 0}
                className="px-6 py-2 bg-primary hover:bg-primary-strong disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {saving ? 'Salvando...' : modal === 'create' ? 'Criar' : 'Salvar'}
              </button>
            </div>
          }
        >
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: CRM IPCOM"
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Descrição</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ex: Integração para sincronização de clientes"
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Scopes *</label>
                <div className="space-y-2">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label key={scope.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.scopes.includes(scope.value)}
                        onChange={() => toggleScope(scope.value)}
                        className="w-4 h-4 rounded border-slate-600 text-orange-500 focus:ring-orange-500 bg-slate-900"
                      />
                      <span className="text-sm text-slate-300">{scope.label}</span>
                      <span className="text-xs text-slate-500 font-mono">{scope.value}</span>
                      {(scope as any).sensitive && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${TONE_BADGE.warning}`}>financeiro</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  IPs permitidos (um por linha, aceita CIDR, vazio = qualquer)
                </label>
                <textarea
                  value={form.allowedIps}
                  onChange={(e) => setForm({ ...form, allowedIps: e.target.value })}
                  placeholder="192.168.1.1&#10;10.0.0.0/24"
                  rows={3}
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none font-mono text-sm`}
                />
              </div>
              {hasSensitiveScope(form.scopes) && (() => {
                const ipsOk = form.allowedIps.trim().length > 0;
                const clientIds = parseClientIds(form.allowedClientIds);
                const tenancyOk = clientIds.length > 0 || form.unrestrictedBillingDocumentsAccess;
                return (
                  <div className={`rounded-lg border p-3 space-y-3 ${TONE_BORDER.warning} ${TONE_SURFACE.warning}`}>
                    <div className={`text-xs font-medium ${TONE_TEXT.warning}`}>🔒 Scopes financeiros exigem contenção</div>
                    <p className="text-[11px] text-slate-400 leading-snug">
                      Para <span className="font-mono">boletos:read</span> / <span className="font-mono">fiscal:read</span> o backend exige uma <b>allowlist de IP</b> (acima, IPv4 puro — não use notação <span className="font-mono">::ffff:</span> nem <span className="font-mono">/0</span>) <b>e</b> restrição de clientes: informe os IDs abaixo <b>ou</b> marque acesso irrestrito.
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">
                        Clientes permitidos (IDs separados por vírgula)
                      </label>
                      <input
                        type="text"
                        value={form.allowedClientIds}
                        onChange={(e) => setForm({ ...form, allowedClientIds: e.target.value })}
                        placeholder="12, 45, 305"
                        disabled={form.unrestrictedBillingDocumentsAccess}
                        className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none font-mono text-sm disabled:opacity-40`}
                      />
                      {clientIds.length > 0 && (
                        <p className="text-[10px] text-slate-500 mt-1">{clientIds.length} cliente(s): {clientIds.join(', ')}</p>
                      )}
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.unrestrictedBillingDocumentsAccess}
                        onChange={(e) => setForm({ ...form, unrestrictedBillingDocumentsAccess: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 text-orange-500 focus:ring-orange-500 bg-slate-900"
                      />
                      <span className="text-sm text-slate-300">Acesso irrestrito a todos os clientes</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${TONE_BADGE.danger}`}>cuidado</span>
                    </label>
                    {(!ipsOk || !tenancyOk) && (
                      <div className={`text-[11px] space-y-0.5 ${TONE_TEXT.danger}`}>
                        {!ipsOk && <div>• Falta a allowlist de IP (obrigatória para scopes financeiros).</div>}
                        {!tenancyOk && <div>• Informe ao menos 1 cliente OU marque acesso irrestrito.</div>}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Rate limit (req/min)</label>
                  <input
                    type="number"
                    value={form.rateLimitPerMinute}
                    onChange={(e) => setForm({ ...form, rateLimitPerMinute: parseInt(e.target.value) || 60 })}
                    min={1}
                    max={1000}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Expiração{' '}
                    <span className="text-slate-500 text-xs">(opcional)</span>
                  </label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                  {modal === 'edit' && form.expiresAt && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, expiresAt: '' })}
                      className="text-xs text-slate-500 hover:text-slate-300 mt-1"
                    >
                      Remover expiração
                    </button>
                  )}
                </div>
              </div>
        </CascaDeModal>
      )}

      {/* Key Display Modal */}
      {modal === 'key' && (
        // ⛔ A chave aparece UMA vez. Antes um clique fora fechava e a chave se perdia
        // para sempre; agora só sai pelo × ou por "Entendi, fechar".
        <CascaDeModal
          largura="sm"
          titulo="Chave de API gerada"
          onClose={closeModal}
          corpoClassName="p-6 space-y-4"
          rodape={
            <button
              onClick={closeModal}
              className="ml-auto px-6 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg transition-colors"
            >
              Entendi, fechar
            </button>
          }
        >
              <div className={`border rounded-lg p-4 ${TONE_SURFACE.danger} ${TONE_BORDER.danger}`}>
                <p className={`text-sm font-medium ${TONE_TEXT.danger}`}>
                  Esta chave será exibida apenas uma vez. Copie e guarde em local seguro.
                </p>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={newKey}
                  readOnly
                  className={`w-full px-4 py-3 pr-20 text-green-400 rounded-lg font-mono text-sm ${campo}`}
                />
                <button
                  onClick={copyToClipboard}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded text-sm transition-colors ${
                    copied ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <div className="text-slate-400 text-sm">
                <p>Use esta chave no header de autenticação:</p>
                <code className="block mt-2 bg-central-fundo rounded p-3 text-slate-300 text-xs break-all">
                  Authorization: Bearer {newKey.slice(0, 20)}...
                </code>
              </div>
        </CascaDeModal>
      )}

      {/* Revoke Modal */}
      {modal === 'revoke' && selectedApp && (
        // Motivo digitado: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
        // "Revogar" segue vermelho (irreversível).
        <CascaDeModal
          largura="xs"
          titulo="Revogar Integration App"
          subtitulo={selectedApp.name}
          onClose={closeModal}
          corpoClassName="p-6 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleRevoke}
                disabled={saving}
                className="px-6 py-2 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 text-white rounded-lg transition-colors"
              >
                {saving ? 'Revogando...' : 'Revogar'}
              </button>
            </div>
          }
        >
              <p className="text-slate-300">
                Tem certeza que deseja revogar <strong className="text-white">"{selectedApp.name}"</strong>?
                Esta ação é irreversível e a chave de API deixará de funcionar imediatamente.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Motivo (opcional)</label>
                <input
                  type="text"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Ex: Substituída por nova integração"
                  className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
        </CascaDeModal>
      )}

      {/* Rotate Modal */}
      {modal === 'rotate' && selectedApp && (
        // Confirmação sem nada digitado: fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="xs"
          fecharAoClicarFora
          titulo="Rotacionar chave de API"
          subtitulo={selectedApp.name}
          onClose={closeModal}
          corpoClassName="p-6 space-y-3"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleRotateKey}
                disabled={saving}
                // irreversível e com efeito em terceiros: as 24h são carência, não desfazer
                className="px-6 py-2 bg-red-600 hover:bg-red-500 disabled:bg-slate-600 text-white rounded-lg transition-colors"
              >
                {saving ? 'Gerando...' : 'Rotacionar'}
              </button>
            </div>
          }
        >
              <p className="text-slate-300">
                Gerar uma nova chave para <strong className="text-white">"{selectedApp.name}"</strong>?
              </p>
              <p className="text-slate-400 text-sm">
                A chave atual continuará válida por <strong className="text-white">24 horas</strong>, pra
                dar tempo de atualizar o consumidor. Após esse prazo, só a nova chave funciona.
              </p>
              <p className="text-slate-400 text-sm">
                Qualquer integração que ainda estiver usando a chave atual <strong className="text-white">para de funcionar</strong> quando
                o prazo acabar. Não dá para voltar à chave antiga.
              </p>
        </CascaDeModal>
      )}

      {/* Toast / Notification */}
      {notification && (
        <div
          className={`fixed bottom-6 right-6 z-[60] max-w-sm rounded-lg shadow-lg border px-4 py-3 flex items-start gap-3 ${
            notification.type === 'success'
              ? 'bg-green-500/10 border-green-500/30 text-green-200'
              : notification.type === 'error'
              ? 'bg-red-500/10 border-red-500/30 text-red-200'
              : 'bg-slate-700 border-slate-600 text-slate-200'
          }`}
        >
          <span className="text-sm flex-1">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="text-current opacity-60 hover:opacity-100"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}
